import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "./firebase";
import { auth } from "./firebase";

import MessageBox from "./MessageBox";

const ReportItem = () => {
  const navigate = useNavigate();
  const [itemType, setItemType] = useState("lost");
  const [lostItemName, setLostItemName] = useState("");
  const [foundItemName, setFoundItemName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");
  const [building, setBuilding] = useState("");
  const [classroom, setClassroom] = useState("");
  const [dateReported, setDateReported] = useState("");
  const [timeReported, setTimeReported] = useState("");
  const [foundNotes, setFoundNotes] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState("");

  const logoutTimerRef = useRef(null);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
    }
    navigate("/login");
  }, [navigate]);

  const resetLogoutTimer = useCallback(() => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
    }
    logoutTimerRef.current = setTimeout(() => {
      handleLogout();
    }, 60 * 60 * 1000);
  }, [handleLogout]);

  const handleUserActivity = useCallback(() => {
    resetLogoutTimer();
  }, [resetLogoutTimer]);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });

    const storedUsername = localStorage.getItem("username");
    if (storedUsername) {
      setUsername(storedUsername);
      resetLogoutTimer();
      window.addEventListener("mousemove", handleUserActivity);
      window.addEventListener("keypress", handleUserActivity);
      window.addEventListener("scroll", handleUserActivity);
      window.addEventListener("click", handleUserActivity);
    } else {
      navigate("/login");
    }

    return () => {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
      }
      window.removeEventListener("mousemove", handleUserActivity);
      window.removeEventListener("keypress", handleUserActivity);
      window.removeEventListener("scroll", handleUserActivity);
      window.removeEventListener("click", handleUserActivity);
    };
  }, [navigate, resetLogoutTimer, handleUserActivity]);

  const showMessageBox = useCallback((msg, type = "info") => {
    setMessage(msg);
    setMessageType(type);
  }, []);

  const closeMessageBox = useCallback(() => {
    setMessage("");
    setMessageType("info");
  }, []);

  // ✅ FIXED: Complete handleSubmit function with userId field
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    const currentUser = auth.currentUser;
    if (!currentUser) {
      showMessageBox("User not logged in. Please log in to report an item.", "danger");
      setLoading(false);
      return;
    }

    const itemName = itemType === "lost" ? lostItemName : foundItemName;
    if (!itemName) {
      showMessageBox(`Please enter the name of the ${itemType} item.`, "danger");
      setLoading(false);
      return;
    }

    if (!description || !category || !location || !building || !classroom || !dateReported) {
      showMessageBox("Please fill in all required fields.", "danger");
      setLoading(false);
      return;
    }

    let imageUrl = null;
    if (imageFile) {
      try {
        const imageRef = ref(storage, `item_images/${Date.now()}-${imageFile.name}`);
        const uploadResult = await uploadBytes(imageRef, imageFile);
        imageUrl = await getDownloadURL(uploadResult.ref);
      } catch (uploadError) {
        console.log(uploadError);
        showMessageBox("Failed to upload image. Please try again or proceed without an image.", "warning");
        imageUrl = null;
      }
    }

    // ✅ CRITICAL FIX: Added missing userId field
    const newItem = {
      type: itemType,
      itemName: itemName,
      description,
      category,
      location,
      building,
      classroom,
      reportedDate: dateReported,
      reportedTime: timeReported || null,
      foundNotes: itemType === "found" ? foundNotes : "",
      imageUrl: imageUrl,
      status: itemType === "lost" ? "lost" : "unclaimed",
      timestamp: serverTimestamp(),
      username: username || currentUser?.displayName || currentUser?.email || "Unknown",
      userId: currentUser.uid, // ✅ THIS WAS MISSING! Critical fix for delete permissions
      currentLocation: itemType === "found" ? "With Founder" : null // Default location for found items
    };

    try {
      await addDoc(collection(db, "items"), newItem);
      showMessageBox(`Successfully reported your ${itemType} item.`, "success");

      // Reset form fields after successful submission
      setItemType("lost");
      setLostItemName("");
      setFoundItemName("");
      setDescription("");
      setCategory("");
      setLocation("");
      setBuilding("");
      setClassroom("");
      setDateReported("");
      setTimeReported("");
      setFoundNotes("");
      setImageFile(null);

      setTimeout(() => {
        navigate("/homepage");
      }, 1400);
    } catch (err) {
      console.log(err);
      showMessageBox("Failed to save item. Please try again.", "danger");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[url('/bg_4.jpg')] bg-cover bg-center h-screen">
      <div>
        {/* Navbar */}
        <nav className=" flex items-center bg-white border-b px-6 py-3 shadow-xl sticky top-0 z-50 animate-fadeIn"
          data-aos="zoom-out"
          style={{
            backgroundImage: "url('/bg_4.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}>
          <Link className="mr-4" to="/homepage" data-aos="zoom-in" data-aos-delay="150">
            <img
              src="/Christ_logo_latest.png"
              alt="Christ University Logo"
              className="logo-christ max-w-[170px] h-auto transition-transform duration-200 hover:scale-105"
            />
          </Link>
          <div className="flex items-center ml-auto space-x-2">
            <span className="font-semibold text-gray-500 mr-5 text-base"
              data-aos="fade-left" data-aos-delay="320">
              Welcome: <span className="text-blue-800">{username}</span>
            </span>
            <ul className="flex items-center gap-2" data-aos="fade-left" data-aos-delay="400">
              <li>
                <Link className="my-nav-link no-underline px-4 py-2 font-semibold border-b-2 border-transparent transition-all hover:border-blue-600 focus-visible:outline-none hover:text-blue-700 focus-visible:text-blue-700"
                  to="/homepage">
                  HOME
                </Link>
              </li>
              <li>
                <Link className="my-nav-link px-4 py-2 font-semibold border-b-2 border-transparent transition-all hover:border-blue-600 focus-visible:outline-none hover:text-blue-700 focus-visible:text-blue-700"
                  to="/history">
                  HISTORY
                </Link>
              </li>
              <li>
                <a
                  className="my-nav-link px-4 py-2 font-semibold border-b-2 border-transparent transition-all hover:border-blue-600 focus-visible:outline-none hover:text-blue-700 focus-visible:text-blue-700"
                  href="https://www.christuniversity.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ABOUT
                </a>
              </li>
              <li>
                <button
                  onClick={handleLogout}
                  type="button"
                  className=" text-center w-48 rounded-2xl h-14 relative text-black text-xl font-semibold group"
                  data-aos="fade-left"
                  data-aos-delay="460"
                >
                  <div
                    className="bg-red-400 rounded-xl h-12 w-1/4 flex items-center justify-center absolute left-1 top-[4px] group-hover:w-[184px] z-10 duration-500"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 1024 1024"
                      height="25px"
                      width="25px"
                    >
                      <path
                        d="M224 480h640a32 32 0 1 1 0 64H224a32 32 0 0 1 0-64z"
                        fill="#000000"
                      ></path>
                      <path
                        d="m237.248 512 265.408 265.344a32 32 0 0 1-45.312 45.312l-288-288a32 32 0 0 1 0-45.312l288-288a32 32 0 1 1 45.312 45.312L237.248 512z"
                        fill="#000000"
                      ></path>
                    </svg>
                  </div>
                  <p className="translate-x-2 wy-2">LOGOUT</p>
                </button>
              </li>
            </ul>
          </div>
        </nav>
        <section className="min-h-screen bg-gray-50 py-8 px-4 flex flex-col items-center font-serif">
          <div className="w-full max-w-2xl bg-white shadow-xl rounded-2xl p-8 relative animate-fadeIn" data-aos="fade-up">
            <h2 className="text-3xl font-extrabold text-blue-900 mb-2 text-center drop-shadow" data-aos="fade-in">
              Report {itemType === "lost" ? "a Lost" : "a Found"} Item
            </h2>
            {message && (
              <MessageBox message={message} type={messageType} onClose={closeMessageBox} />
            )}

            <form className="space-y-6 mt-6" onSubmit={handleSubmit} data-aos="zoom-in" data-aos-delay="200" autoComplete="off">
              <div className="flex justify-center items-center gap-4 mb-8">
                <button
                  type="button"
                  className={`px-6 py-2 rounded-full font-bold border-2 transition-all duration-200 ${
                    itemType === "lost"
                      ? "bg-red-700 text-white border-red-700 scale-105 shadow-md"
                      : "bg-white text-blue-700 border-blue-600"
                  }`}
                  onClick={() => setItemType("lost")}
                >Lost</button>
                <button
                  type="button"
                  className={`px-6 py-2 rounded-full font-bold border-2 transition-all duration-200 ${
                    itemType === "found"
                      ? "bg-green-700 text-white border-green-700 scale-105 shadow-md"
                      : "bg-white text-green-700 border-green-600"
                  }`}
                  onClick={() => setItemType("found")}
                >Found</button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-gray-700 mb-1 font-semibold" htmlFor="itemName">
                    Item Name<span className="text-red-500">*</span>
                  </label>
                  <input
                    id="itemName"
                    className="w-full border border-gray-300 rounded px-4 py-2 focus:ring-2 focus:ring-blue-400 transition outline-none"
                    value={itemType === "lost" ? lostItemName : foundItemName}
                    onChange={e => itemType === "lost"
                      ? setLostItemName(e.target.value)
                      : setFoundItemName(e.target.value)}
                    name="itemName"
                    placeholder={`Enter ${itemType} item name`}
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-semibold" htmlFor="category">
                    Category<span className="text-red-500">*</span>
                  </label>
                  <input
                    id="category"
                    className="w-full border border-gray-300 rounded px-4 py-2 focus:ring-2 focus:ring-blue-400 transition outline-none"
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                    name="category"
                    placeholder="e.g. Wallet, Bag, Phone"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 mb-1 font-semibold" htmlFor="description">
                  Description<span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  className="w-full border border-gray-300 rounded px-4 py-2 focus:ring-2 focus:ring-blue-400 transition outline-none min-h-[80px]"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  name="description"
                  placeholder="Describe the item, distinct features, etc."
                ></textarea>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-gray-700 mb-1 font-semibold" htmlFor="location">
                    Location<span className="text-red-500">*</span>
                  </label>
                  <input
                    id="location"
                    className="w-full border border-gray-300 rounded px-4 py-2 focus:ring-2 focus:ring-blue-400 transition outline-none"
                    value={location}
                    onChange={e => setLocation(e.target.value)}
                    name="location"
                    placeholder="Campus Area (e.g., Library, Cafeteria)"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-semibold" htmlFor="building">
                    Building<span className="text-red-500">*</span>
                  </label>
                  <input
                    id="building"
                    className="w-full border border-gray-300 rounded px-4 py-2 focus:ring-2 focus:ring-blue-400 transition outline-none"
                    value={building}
                    onChange={e => setBuilding(e.target.value)}
                    name="building"
                    placeholder="Building Name/Number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-gray-700 mb-1 font-semibold" htmlFor="classroom">
                    Classroom<span className="text-red-500">*</span>
                  </label>
                  <input
                    id="classroom"
                    className="w-full border border-gray-300 rounded px-4 py-2 focus:ring-2 focus:ring-blue-400 transition outline-none"
                    value={classroom}
                    onChange={e => setClassroom(e.target.value)}
                    name="classroom"
                    placeholder="Room/Section (e.g., 205-A)"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-semibold" htmlFor="dateReported">
                    Date<span className="text-red-500">*</span>
                  </label>
                  <input
                    id="dateReported"
                    type="date"
                    className="w-full border border-gray-300 rounded px-4 py-2 focus:ring-2 focus:ring-blue-400 transition outline-none"
                    value={dateReported}
                    onChange={e => setDateReported(e.target.value)}
                    name="dateReported"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-gray-700 mb-1 font-semibold" htmlFor="timeReported">
                    Time
                  </label>
                  <input
                    id="timeReported"
                    type="time"
                    className="w-full border border-gray-300 rounded px-4 py-2 focus:ring-2 focus:ring-blue-400 transition outline-none"
                    value={timeReported}
                    onChange={e => setTimeReported(e.target.value)}
                    name="timeReported"
                  />
                </div>
                {itemType === "found" && (
                  <div>
                    <label className="block text-gray-700 mb-1 font-semibold" htmlFor="foundNotes">
                      Found Item Additional Notes
                    </label>
                    <input
                      id="foundNotes"
                      className="w-full border border-gray-300 rounded px-4 py-2 focus:ring-2 focus:ring-green-400 transition outline-none"
                      value={foundNotes}
                      onChange={e => setFoundNotes(e.target.value)}
                      name="foundNotes"
                      placeholder="e.g. Where/how found, special info"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-gray-700 mb-1 font-semibold" htmlFor="imageUpload">
                  Image (Optional)
                </label>
                <input
                  id="imageUpload"
                  type="file"
                  className="w-full p-2 bg-gray-50 border border-gray-300 rounded transition"
                  accept="image/*"
                  onChange={e => setImageFile(e.target.files[0])}
                />
              </div>

              <div className="flex justify-between items-center mt-8">
                <button
                  type="submit"
                  className={`px-8 py-3 rounded-pill font-bold text-lg shadow-xl transition-all duration-200 
                    ${
                      loading
                        ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                        : itemType === "lost"
                        ? "bg-blue-700 text-white hover:bg-blue-800 hover:scale-105"
                        : "bg-green-700 text-white hover:bg-green-800 hover:scale-105"
                    }
                  `}
                  disabled={loading}
                  data-aos="flip-left"
                >
                  {loading ? "Submitting..." : `Submit ${itemType === "lost" ? "Lost" : "Found"} Item`}
                </button>
                <Link
                  to="/homepage"
                  className="my-nav-link text-base px-6 py-2 rounded-pill border font-bold border-gray-400 text-gray-700 bg-gray-100 hover:text-blue-600 hover:border-blue-600 transition"
                  data-aos="fade-left"
                  data-aos-delay="200"
                >
                  Cancel
                </Link>
              </div>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ReportItem;
